import type Redis from 'ioredis';

export function buildProductCacheModeSuffix(opts: {
  applyDailyLimitEffectiveStock?: boolean;
  enableHighestValuationTypePricing?: boolean;
  newBuyerVoucherEnabled?: boolean;
}): string {
  let suffix = '';
  if (opts.applyDailyLimitEffectiveStock) suffix += ':daily-order-limit';
  if (opts.enableHighestValuationTypePricing) suffix += ':hvtp';
  if (opts.newBuyerVoucherEnabled) suffix += ':nbv';
  return suffix;
}

export function productCacheVersionKey(itemCode: number): string {
  return `productVer/${itemCode}`;
}

export function legacyProductCacheKey(
  itemCode: number,
  sapNoShipto: number
): string {
  return `product/${itemCode}_${sapNoShipto}`;
}

export function productCacheKey(
  itemCode: number,
  sapNoShipto: number,
  version: number,
  includeVoucherCount = false
): string {
  const baseKey = `product/${itemCode}_${sapNoShipto}:v${version}`;
  return includeVoucherCount ? `${baseKey}:vc` : baseKey;
}

export async function getProductCacheVersion(
  redis: Redis,
  itemCode: number
): Promise<number> {
  const raw = await redis.get(productCacheVersionKey(itemCode));
  const n = raw === null ? 0 : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export async function getManyProductCacheVersions(
  redis: Redis,
  itemCodes: number[]
): Promise<Map<number, number>> {
  const uniqueItemCodes = Array.from(new Set(itemCodes));
  const pipeline = redis.pipeline();
  for (const itemCode of uniqueItemCodes) {
    pipeline.get(productCacheVersionKey(itemCode));
  }
  const res = await pipeline.exec();

  const versions = new Map<number, number>();
  for (let i = 0; i < uniqueItemCodes.length; i++) {
    const itemCode = uniqueItemCodes[i];
    const raw = res?.[i]?.[1] as string | null | undefined;
    const n = raw == null ? 0 : Number(raw);
    versions.set(itemCode, Number.isFinite(n) ? n : 0);
  }

  return versions;
}

export async function bumpManyProductCacheVersions(
  redis: Redis,
  itemCodes: number[],
  expireSeconds = 60 * 60 * 24 // 1 day
): Promise<Map<number, number>> {
  const uniqueItemCodes = Array.from(new Set(itemCodes));
  const pipeline = redis.pipeline();
  const versions = new Map<number, number>();

  // Step 1: Increment all keys.
  for (const itemCode of uniqueItemCodes) {
    const key = productCacheVersionKey(itemCode);
    pipeline.incr(key);
  }
  const res = await pipeline.exec();

  // Step 2: Set expiry ONLY for keys that were just created (value === 1).
  let expiryPipeline: ReturnType<typeof redis.pipeline> | null = null;

  for (let i = 0; i < uniqueItemCodes.length; i++) {
    const itemCode = uniqueItemCodes[i];
    const n = Number(res?.[i]?.[1]);
    const version = Number.isFinite(n) ? n : 0;

    versions.set(itemCode, version);

    if (version === 1) {
      if (!expiryPipeline) {
        expiryPipeline = redis.pipeline();
      }
      const key = productCacheVersionKey(itemCode);
      expiryPipeline.expire(key, expireSeconds);
    }
  }

  if (expiryPipeline) {
    await expiryPipeline.exec();
  }

  return versions;
}
